import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ArrowRight, Landmark, Wallet, Receipt } from 'lucide-react';

const FINANCIAL_FIELDS = [
  { field: 'annual_agricultural_income', label: 'Annual Agri Income', format: 'currency' },
  { field: 'other_income_pct', label: 'Other Income (%)', format: 'percent' },
  { field: 'total_outstanding_debt', label: 'Outstanding Debt', format: 'currency' },
  { field: 'avg_bank_balance_6mo', label: 'Avg Bank Balance (6m)', format: 'currency' },
  { field: 'bank_account_vintage_years', label: 'Account Vintage (Yrs)', format: 'number' },
  { field: 'banking_transactions_count', label: 'Banking Txns (12m)', format: 'number' },
  { field: 'kcc_transaction_frequency', label: 'KCC Txn Frequency', format: 'number' },
  { field: 'annual_income_retention_ratio_pct', label: 'Income Retention (%)', format: 'percent' },
];

const FinancialUpdate = ({ farmer, assessment, previousAssessment }) => {
  const prevSnapshot = previousAssessment?.farmer_profile_snapshot || {};
  const changedFields = new Set(
    (assessment?.changed_fields || []).filter(cf => cf.section === 'financial').map(cf => cf.field)
  );

  const formatVal = (val, format) => {
    if (val === null || val === undefined) return 'N/A';
    switch (format) {
      case 'currency': return `Rs. ${Number(val).toLocaleString()}`;
      case 'percent': return `${val}%`;
      case 'number': return Number(val).toLocaleString();
      default: return String(val);
    }
  };

  const totalChanges = (assessment?.changed_fields || []).filter(cf => cf.section === 'financial').length;

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1" data-testid="financial-update-title">
          Financial & Liability Update
        </h3>
        <p className="text-sm text-gray-500">
          Review updated financial position and liability evidence. Changes from the previous cycle are highlighted.
        </p>
      </div>

      {totalChanges > 0 && (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2 border border-amber-200">
          <AlertTriangle className="h-4 w-4" />
          {totalChanges} financial field(s) changed since last cycle. Stronger validation applies.
        </div>
      )}

      {/* Side-by-side Financial Comparison */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 mb-3">
            <Landmark className="h-4 w-4 text-gray-600" />
            <span className="font-semibold text-xs text-gray-500 uppercase tracking-wide">Financial Comparison</span>
          </div>

          <div className="grid grid-cols-4 gap-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide pb-2 border-b border-gray-200 mb-2">
            <span>Metric</span>
            <span className="text-right">Previous</span>
            <span className="text-center"></span>
            <span>Current</span>
          </div>

          {FINANCIAL_FIELDS.map(({ field, label, format }) => {
            const prevVal = prevSnapshot[field];
            const currVal = farmer?.[field];
            const isChanged = changedFields.has(field);

            return (
              <div key={field} className={`grid grid-cols-4 gap-2 items-center py-2 border-b border-gray-50 ${isChanged ? 'bg-amber-50/50 -mx-2 px-2 rounded' : ''}`}>
                <span className="text-xs text-gray-700 font-medium flex items-center gap-1">
                  {isChanged && <AlertTriangle className="h-3 w-3 text-amber-500" />}
                  {label}
                </span>
                <span className={`text-xs text-right ${isChanged ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
                  {formatVal(prevVal, format)}
                </span>
                <div className="flex justify-center">
                  {isChanged && <ArrowRight className="h-3 w-3 text-amber-500" />}
                </div>
                <span className={`text-xs font-medium ${isChanged ? 'text-amber-700' : 'text-gray-800'}`}>
                  {formatVal(currVal, format)}
                </span>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* CBS Data Summary if available */}
      {assessment?.cbs_refresh_data && (
        <Card className="border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Wallet className="h-4 w-4 text-green-700" />
              <span className="font-semibold text-xs text-green-800 uppercase tracking-wide">CBS Refreshed Data</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                ['Sanctioned Limit', formatVal(assessment.cbs_refresh_data.sanctioned_limit, 'currency')],
                ['Outstanding Amount', formatVal(assessment.cbs_refresh_data.outstanding_amount ?? assessment.cbs_refresh_data.current_outstanding, 'currency')],
                ['Utilization', `${assessment.cbs_refresh_data.utilization_pct}%`],
                ['Cumulative Withdrawal Amount', formatVal(assessment.cbs_refresh_data.cumulative_withdrawal_amount, 'currency')],
                ['Cumulative Repayment Amount', formatVal(assessment.cbs_refresh_data.cumulative_repayment_amount ?? assessment.cbs_refresh_data.repayment_turnover, 'currency')],
                ['No. of Defaults', formatVal(assessment.cbs_refresh_data.no_of_defaults ?? (assessment.cbs_refresh_data.default_event_flag ? 1 : 0), 'number')],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between text-xs py-1.5 border-b border-gray-100">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-medium text-gray-900">{val}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md border border-gray-200">
        For enhanced limit assessments, changes in income, debt, and banking activity receive stricter validation.
        Ensure supporting documents are uploaded for material changes.
      </div>
    </div>
  );
};

export default FinancialUpdate;
